import { DateTime } from "luxon";
import { ManagedReceivingPartnerApi as Api } from "@capstone/mock-api";
import { Day } from "src/app/core/day.model";
import { formatAmount } from "src/app/core/pipes/amount.pipe";
import { formatTimeOfDay } from "src/app/core/pipes/time-of-day.pipe";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import {
  getApiDetailsDecorator,
  getUpdatedPropertiesFilter,
  isExistent,
} from "src/utils";

const api = getApiDetailsDecorator<Api.HourlyDockCapacity>();

abstract class BaseHourlyDockCapacity {
  protected constructor(args: ClassProperties<BaseHourlyDockCapacity>) {
    this.appointment = args.appointment;
    this.case = args.case;
    this.endTime = args.endTime;
    this.pallet = args.pallet;
    this.site = args.site;
    this.startTime = args.startTime;
    this.weekdays = args.weekdays;
  }

  @api({ key: "appointmentCapacity" })
  public readonly appointment: number | null;
  @api({ key: "caseCapacity" }) public readonly case: number | null;
  @api() public readonly endTime: TimeOfDay;
  @api({ key: "palletCapacity" }) public readonly pallet: number | null;
  public readonly site: Site;
  @api() public readonly startTime: TimeOfDay;
  @api({ key: "dayOfWeek" }) public readonly weekdays: Weekdays;

  public isApplicableAt(time: DateTime): boolean {
    return (
      this.weekdays.isSelected(time) &&
      new Day(time)
        .toInterval(time.zone, this.startTime, this.endTime)
        .contains(time)
    );
  }
}

export class HourlyDockCapacity extends BaseHourlyDockCapacity {
  private constructor(args: Omit<ClassProperties<HourlyDockCapacity>, "name">) {
    super(args);
    this.id = args.id;
    this.name = formatHourlyCapacityName(this);
  }

  public readonly id: number;
  public readonly name: string;

  public static deserialize(
    data: Api.HourlyDockCapacity,
    { site }: DeserializeArguments,
  ): HourlyDockCapacity | HourlyDockCapacityException {
    return data.precedence > 0
      ? HourlyDockCapacityException.deserialize(data, { site })
      : new HourlyDockCapacity({
          appointment: data.appointmentCapacity ?? null,
          case: data.caseCapacity ?? null,
          endTime: TimeOfDay.deserialize(data.endTime),
          id: data.id,
          pallet: data.palletCapacity ?? null,
          site,
          startTime: TimeOfDay.deserialize(data.startTime),
          weekdays: Weekdays.deserialize(data.dayOfWeek),
        });
  }
}

export class HourlyDockCapacityUpdate extends BaseHourlyDockCapacity {
  private constructor({ base, ...args }: HourlyDockCapacityUpdateArguments) {
    super(args);

    this.base = base;
    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: HourlyDockCapacity | null;
  private readonly filterUpdatedProperties: FilterPartial<Api.HourlyDockCapacityUpdate>;

  public static create(
    args: HourlyDockCapacityUpdateArguments,
  ): HourlyDockCapacityUpdate {
    return new HourlyDockCapacityUpdate({
      ...args,
      site: args.base?.site ?? args.site,
    });
  }

  public serialize(): Partial<Api.HourlyDockCapacityUpdate> {
    const dayOfWeek = this.weekdays.serialize();
    if (!dayOfWeek) {
      throw new Error(
        "Hourly dock capacities must have at least one selected day.",
      );
    }
    return this.filterUpdatedProperties({
      appointmentCapacity: this.appointment,
      caseCapacity: this.case,
      dayOfWeek,
      endDate: null,
      endTime: this.endTime.serialize(),
      name: null,
      palletCapacity: this.pallet,
      // Regular, non-exception capacities have a precedence of zero.
      precedence: 0,
      startDate: null,
      startTime: this.startTime.serialize(),
    });
  }
}

export type HourlyDockCapacityUpdateArguments =
  ClassProperties<HourlyDockCapacityUpdate>;

abstract class BaseHourlyDockCapacityException extends BaseHourlyDockCapacity {
  protected constructor(
    args: ClassProperties<BaseHourlyDockCapacityException>,
  ) {
    super(args);

    this.customName = args.customName;
    this.endDay = args.endDay;
    this.precedence = args.precedence;
    this.startDay = args.startDay;
  }

  @api({ key: "name" }) public readonly customName: string | null;
  @api() public readonly precedence: number;
  @api({ key: "endDate" }) public readonly endDay: Day;
  @api({ key: "startDate" }) public readonly startDay: Day;

  public override isApplicableAt(time: DateTime): boolean {
    const businessDay = this.site.getBusinessDay(time).day;

    return (
      this.weekdays.isSelected(businessDay) &&
      new Day(time)
        .toInterval(time.zone, this.startTime, this.endTime)
        .contains(time) &&
      businessDay.isAfterOrSameDay(this.startDay) &&
      !businessDay.isAfter(this.endDay)
    );
  }
}

export class HourlyDockCapacityException extends BaseHourlyDockCapacityException {
  private constructor(
    args: Omit<ClassProperties<HourlyDockCapacityException>, "name">,
  ) {
    super(args);
    this.id = args.id;
    this.name = this.customName || formatHourlyCapacityName(this);
  }

  public readonly id: number;
  public readonly name: string;

  public static deserialize(
    data: Api.HourlyDockCapacity,
    { site }: DeserializeArguments,
  ): HourlyDockCapacityException {
    if (data.precedence === 0) {
      throw new Error(
        "Hourly dock capacity exceptions cannot have a zero precedence.",
      );
    }
    if (!data.startDate) {
      throw new Error("Start date required for hourly dock capacity.");
    }
    if (!data.endDate) {
      throw new Error("End date required for hourly dock capacity.");
    }

    return new HourlyDockCapacityException({
      appointment: data.appointmentCapacity ?? null,
      case: data.caseCapacity ?? null,
      customName: data.name ?? null,
      endDay: Day.deserialize(data.endDate, site.timeZone),
      endTime: TimeOfDay.deserialize(data.endTime),
      id: data.id,
      pallet: data.palletCapacity ?? null,
      precedence: data.precedence,
      site,
      startDay: Day.deserialize(data.startDate, site.timeZone),
      startTime: TimeOfDay.deserialize(data.startTime),
      weekdays: Weekdays.deserialize(data.dayOfWeek),
    });
  }
}

interface DeserializeArguments {
  readonly site: Site;
}

export class HourlyDockCapacityExceptionUpdate extends BaseHourlyDockCapacityException {
  public constructor({
    base,
    ...args
  }: HourlyDockCapacityExceptionUpdateArguments) {
    super(args);

    this.base = base;
    this.name = this.customName;
    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: HourlyDockCapacityException | null;
  public readonly name: string | null;
  private readonly filterUpdatedProperties: FilterPartial<Api.HourlyDockCapacityUpdate>;

  public serialize(): Partial<Api.HourlyDockCapacityUpdate> {
    const dayOfWeek = this.weekdays.serialize();
    if (!dayOfWeek) {
      throw new Error(
        "Hourly dock capacity exceptions must have at least one selected day.",
      );
    }

    return this.filterUpdatedProperties({
      appointmentCapacity: this.appointment,
      caseCapacity: this.case,
      dayOfWeek,
      endDate: this.endDay?.toString() ?? null,
      endTime: this.endTime.serialize(),
      name: this.customName,
      palletCapacity: this.pallet,
      precedence: this.precedence,
      startDate: this.startDay?.toString() ?? null,
      startTime: this.startTime.serialize(),
    });
  }
}

export type HourlyDockCapacityExceptionUpdateArguments = Omit<
  ClassProperties<HourlyDockCapacityExceptionUpdate>,
  "name"
>;

export function formatHourlyCapacityName({
  weekdays,
  startTime,
  endTime,
  appointment,
  pallet,
  case: caseCapacity,
}: BaseHourlyDockCapacity): string {
  const formattedWeekdays = formatWeekdays(weekdays, "short");
  const formattedStart = formatTimeOfDay(startTime, "hour");
  const formattedEnd = formatTimeOfDay(endTime, "hour");

  return [
    `${formattedWeekdays} ${formattedStart}–${formattedEnd}`,
    appointment !== null ? formatAmount(appointment, "en-US", "Appt") : null,
    pallet !== null ? formatAmount(pallet, "en-US", "Pallet") : null,
    caseCapacity !== null ? formatAmount(caseCapacity, "en-US", "Case") : null,
  ]
    .filter(isExistent)
    .join(" | ");
}
